// Image utility functions for resizing and compressing images

/**
 * Resize and compress image to 200x200 pixels with file size < 100KB
 * @param {File} file - The image file to process
 * @param {number} targetSize - Target file size in bytes (default: 100KB)
 * @param {number} maxWidth - Maximum width in pixels (default: 200)
 * @param {number} maxHeight - Maximum height in pixels (default: 200)
 * @returns {Promise<Blob>} - Processed image blob
 */
export const resizeAndCompressImage = (file, targetSize = 100 * 1024, maxWidth = 200, maxHeight = 200) => {
  return new Promise((resolve, reject) => {
    // Check if file is an image
    if (!file.type.startsWith('image/')) {
      reject(new Error('File is not an image'));
      return;
    }

    const img = new Image();
    const canvas = document.createElement('canvas');
    const ctx = canvas.getContext('2d');

    img.onload = () => {
      // Calculate new dimensions maintaining aspect ratio
      let { width, height } = img;
      
      // Scale down to fit within maxWidth x maxHeight
      const scale = Math.min(maxWidth / width, maxHeight / height);
      width = width * scale;
      height = height * scale;

      // Set canvas size
      canvas.width = width;
      canvas.height = height;

      // Draw and resize image
      ctx.drawImage(img, 0, 0, width, height);

      // Function to get blob with specific quality
      const getBlob = (quality = 0.8) => {
        return new Promise((resolve) => {
          canvas.toBlob(resolve, 'image/jpeg', quality);
        });
      };

      // Binary search for optimal quality to meet size requirement
      const findOptimalQuality = async () => {
        let minQuality = 0.1;
        let maxQuality = 0.9;
        let optimalBlob = null;

        for (let i = 0; i < 10; i++) { // Max 10 iterations
          const quality = (minQuality + maxQuality) / 2;
          const blob = await getBlob(quality);

          if (blob.size <= targetSize) {
            optimalBlob = blob;
            minQuality = quality;
          } else {
            maxQuality = quality;
          }

          // If we're close enough, break
          if (Math.abs(blob.size - targetSize) < 5000) { // Within 5KB
            optimalBlob = blob;
            break;
          }
        }

        return optimalBlob || await getBlob(0.1); // Fallback to lowest quality
      };

      findOptimalQuality()
        .then(resolve)
        .catch(reject);
    };

    img.onerror = () => {
      reject(new Error('Failed to load image'));
    };

    // Load image
    const reader = new FileReader();
    reader.onload = (e) => {
      img.src = e.target.result;
    };
    reader.onerror = () => {
      reject(new Error('Failed to read file'));
    };
    reader.readAsDataURL(file);
  });
};

/**
 * Convert blob to file
 * @param {Blob} blob - The image blob
 * @param {string} filename - Original filename
 * @returns {File} - Processed image file
 */
export const blobToFile = (blob, filename) => {
  const extension = filename.split('.').pop();
  const newFilename = filename.replace(`.${extension}`, '_resized.jpg');
  return new File([blob], newFilename, { type: 'image/jpeg' });
};

/**
 * Process image file: resize to 200x200 and compress to <100KB
 * @param {File} file - Original image file
 * @returns {Promise<File>} - Processed image file
 */
export const processImageFile = async (file) => {
  try {
    const compressedBlob = await resizeAndCompressImage(file);
    return blobToFile(compressedBlob, file.name);
  } catch (error) {
    console.error('Image processing error:', error);
    throw new Error('Failed to process image. Please try a different image.');
  }
};

/**
 * Validate image file before processing
 * @param {File} file - Image file to validate
 * @returns {boolean} - Whether file is valid
 */
export const validateImageFile = (file) => {
  const supportedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/gif', 'image/webp'];
  const maxFileSize = 10 * 1024 * 1024; // 10MB max input size

  if (!supportedTypes.includes(file.type)) {
    throw new Error('Please upload a PNG, JPEG, GIF, or WebP image');
  }

  if (file.size > maxFileSize) {
    throw new Error('Image file is too large. Maximum size is 10MB');
  }

  return true;
};

/**
 * Get image preview URL
 * @param {File} file - Image file
 * @returns {string} - Preview URL
 */
export const getImagePreviewUrl = (file) => {
  return URL.createObjectURL(file);
};

/**
 * Cleanup preview URL to prevent memory leaks
 * @param {string} url - Preview URL to cleanup
 */
export const cleanupPreviewUrl = (url) => {
  if (url && url.startsWith('blob:')) {
    URL.revokeObjectURL(url);
  }
};